import axios, { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';
import { environment } from 'config/environment';

interface MindBodyCredentials {
  username: string;
  password: string;
  siteId: number;
}

interface MindBodyAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    studioIds: string[];
  };
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

class MindBodyAuthService {
  private apiClient: AxiosInstance;
  private readonly TOKEN_KEY = 'cueflow_auth_token';
  private readonly REFRESH_TOKEN_KEY = 'cueflow_refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'cueflow_token_expiry';
  private refreshPromise: Promise<TokenPair> | null = null;

  constructor() {
    this.apiClient = axios.create({
      baseURL: `${environment.api.baseUrl}/${environment.api.version}/auth`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to attach token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newTokens = await this.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            return this.apiClient(originalRequest);
          } catch (refreshError) {
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Login with MindBody credentials
   */
  async login(credentials: MindBodyCredentials): Promise<MindBodyAuthResponse> {
    try {
      const response = await this.apiClient.post<MindBodyAuthResponse>('/mindbody/login', {
        username: credentials.username,
        password: credentials.password,
        siteId: credentials.siteId,
      });

      const { accessToken, refreshToken, expiresIn, user } = response.data;

      // Calculate token expiry time
      const expiresAt = Date.now() + expiresIn * 1000;

      // Store tokens securely
      this.storeTokens({
        accessToken,
        refreshToken,
        expiresAt,
      });

      // Store user info in localStorage
      localStorage.setItem('cueflow_user', JSON.stringify(user));

      return response.data;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Refresh the access token using the refresh token
   */
  async refreshAccessToken(): Promise<TokenPair> {
    // If a refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = this.apiClient
      .post<{ accessToken: string; expiresIn: number }>('/mindbody/refresh', {
        refreshToken,
      })
      .then((response) => {
        const { accessToken, expiresIn } = response.data;
        const expiresAt = Date.now() + expiresIn * 1000;

        const tokens: TokenPair = {
          accessToken,
          refreshToken,
          expiresAt,
        };

        this.storeTokens(tokens);
        this.refreshPromise = null;
        return tokens;
      })
      .catch((error) => {
        this.refreshPromise = null;
        throw error;
      });

    return this.refreshPromise;
  }

  /**
   * Validate current session with MindBody
   */
  async validateSession(): Promise<boolean> {
    try {
      const token = this.getAccessToken();
      if (!token) return false;

      const response = await this.apiClient.get('/mindbody/validate');
      return response.data.valid === true;
    } catch {
      return false;
    }
  }

  /**
   * Logout and clear all auth data
   */
  logout(): void {
    // Clear cookies
    Cookies.remove(this.TOKEN_KEY);
    Cookies.remove(this.REFRESH_TOKEN_KEY);
    Cookies.remove(this.TOKEN_EXPIRY_KEY);

    // Clear localStorage
    localStorage.removeItem('cueflow_user');
    localStorage.removeItem('authToken'); // Legacy token storage
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const expiry = this.getTokenExpiry();
    
    if (!token || !expiry) return false;
    
    // Check if token is expired (with 5 minute buffer)
    return expiry > Date.now() + 5 * 60 * 1000;
  }

  /**
   * Get the current user from storage
   */
  getCurrentUser(): MindBodyAuthResponse['user'] | null {
    const userStr = localStorage.getItem('cueflow_user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Store tokens securely
   */
  private storeTokens(tokens: TokenPair): void {
    // Store in httpOnly cookies for security (in production)
    // For development, we'll use regular cookies
    const isProduction = environment.production;
    const cookieOptions = {
      secure: isProduction,
      sameSite: 'strict' as const,
      expires: new Date(tokens.expiresAt),
    };

    Cookies.set(this.TOKEN_KEY, tokens.accessToken, cookieOptions);
    Cookies.set(this.REFRESH_TOKEN_KEY, tokens.refreshToken, cookieOptions);
    Cookies.set(this.TOKEN_EXPIRY_KEY, tokens.expiresAt.toString(), cookieOptions);

    // Also store in localStorage for API calls
    localStorage.setItem('authToken', tokens.accessToken);
  }

  /**
   * Get access token from storage
   */
  private getAccessToken(): string | undefined {
    return Cookies.get(this.TOKEN_KEY);
  }

  /**
   * Get refresh token from storage
   */
  private getRefreshToken(): string | undefined {
    return Cookies.get(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get token expiry time
   */
  private getTokenExpiry(): number | null {
    const expiry = Cookies.get(this.TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: any): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.response?.status === 401) {
      return new Error('Invalid credentials');
    }
    if (error.response?.status === 403) {
      return new Error('Access denied');
    }
    return new Error('Authentication failed');
  }
}

// Export singleton instance
export const mindBodyAuth = new MindBodyAuthService();